<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { formErrorsFrom } from './formErrors';
import { toast } from 'vue-sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import ModelSearchPicker from '@/components/ModelSearchPicker.vue';
import CreateChoiceField from '@/components/createRecord/CreateChoiceField.vue';
import { useCreateMutation } from '@/composables/api/useCrudApi';
import { modelIcon } from '@/composables/modelIcons';
import { recordPathFor } from '@/navigation/areas';
import { useAuthStore } from '@/stores/authStore';
import { createPayload, firstMissingField, formForUser, initialValues, type CreateForm, type CreateValues } from '@/components/createRecord/createRecordForms';

/**
 * The form Create New opens for one record type. It asks only for the fields
 * a create needs (createRecordForms.ts), posts to `POST /api/<slug>`, and opens
 * the new record's page. When the server refuses a field, its reason shows
 * under that field.
 */
const props = defineProps<{ form: CreateForm }>();
// A clients-only user must name one of their zones where the backend checks it.
const authStore = useAuthStore();
const form = computed(() => formForUser(props.form, Boolean(authStore.userData?.clients_only)));
const open = defineModel<boolean>('open', { required: true });

const router = useRouter();
const queryClient = useQueryClient();
const createMutation = useCreateMutation(computed(() => props.form.slug));

const values = ref<CreateValues>(initialValues(props.form));
const fieldErrors = ref<Record<string, string>>({});
const formError = ref<string | null>(null);

watch([open, () => props.form], ([isOpen]) => {
    if (isOpen) {
        values.value = initialValues(props.form);
        fieldErrors.value = {};
        formError.value = null;
    }
});

const missing = computed(() => firstMissingField(form.value, values.value));
const icon = computed(() => modelIcon(props.form.entityId));

async function submit() {
    if (missing.value || createMutation.isPending.value) return;
    fieldErrors.value = {};
    formError.value = null;
    try {
        const response = await createMutation.mutateAsync(createPayload(form.value, values.value, authStore.userData?.id ?? null));
        // A resource answers `{ data: {...} }`; allow a bare record too.
        const body = response.data as { data?: unknown } | undefined;
        const created = ((body && typeof body === 'object' && 'data' in body ? body.data : body) ?? {}) as { id?: number; display_string?: string; name?: string };
        // Lists of this type are now out of date.
        await queryClient.invalidateQueries({ queryKey: ['search'] });
        open.value = false;
        toast.success(`Created ${created.display_string ?? created.name ?? props.form.title.replace(/^New /, '')}.`);
        if (typeof created.id === 'number') {
            void router.push(recordPathFor(props.form.entityId, created.id));
        }
    } catch (error) {
        ({ fieldErrors: fieldErrors.value, formError: formError.value } = formErrorsFrom(
            error,
            props.form.fields.map((field) => field.name),
            { forbidden: "You don't have permission to create this here.", fallback: 'Could not create it. Try again.' },
        ));
    }
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-lg" data-testid="create-record-dialog">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2.5">
          <component :is="icon" v-if="icon" class="size-5" aria-hidden="true" />
          {{ form.title }}
        </DialogTitle>
        <DialogDescription>Everything else can be set on the new record's page.</DialogDescription>
      </DialogHeader>

      <form class="flex flex-col gap-4" novalidate @submit.prevent="submit">
        <div v-for="field in form.fields" :key="field.name" class="flex flex-col gap-1.5">
          <label v-if="field.kind === 'boolean'" class="flex items-center gap-2 text-sm">
            <Checkbox
              :model-value="Boolean(values[field.name])"
              :data-testid="`create-field-${field.name}`"
              @update:model-value="(checked: boolean | 'indeterminate') => (values[field.name] = checked === true)"
            />
            {{ field.label }}
          </label>
          <template v-else>
            <label :for="`create-${field.name}`" class="text-sm font-medium">
              {{ field.label }}<span v-if="!field.required" class="font-normal text-muted-foreground"> (optional)</span>
            </label>
            <Textarea
              v-if="field.kind === 'textarea'"
              :id="`create-${field.name}`"
              v-model="values[field.name] as string"
              :data-testid="`create-field-${field.name}`"
            />
            <CreateChoiceField v-else-if="field.kind === 'choice'" v-model="values[field.name]" :field="field" :input-id="`create-${field.name}`" />
            <ModelSearchPicker
              v-else-if="field.kind === 'record'"
              :model-value="values[field.name] === null ? null : String(values[field.name])"
              :slug="field.recordSlug!"
              :test-id-prefix="`create-field-${field.name}`"
              @update:model-value="(id: string | null) => (values[field.name] = id === null ? null : Number(id))"
            />
            <Input
              v-else
              :id="`create-${field.name}`"
              v-model="values[field.name] as string"
              :type="field.kind === 'number' ? 'number' : field.kind"
              :min="field.min"
              :max="field.max"
              autocomplete="off"
              :data-testid="`create-field-${field.name}`"
            />
          </template>
          <p v-if="fieldErrors[field.name]" class="text-sm text-destructive-foreground" role="alert" :data-testid="`create-error-${field.name}`">
            {{ fieldErrors[field.name] }}
          </p>
          <p v-else-if="field.hint" class="text-xs text-muted-foreground">{{ field.hint }}</p>
        </div>
        <p v-if="formError" class="text-sm text-destructive-foreground" role="alert" data-testid="create-form-error">{{ formError }}</p>
        <button type="submit" class="hidden" />
      </form>

      <DialogFooter>
        <Button variant="outline" @click="open = false">Cancel</Button>
        <Button
          variant="accent"
          :disabled="Boolean(missing) || createMutation.isPending.value"
          :title="missing ? `Fill in ${missing} first.` : undefined"
          data-testid="create-record-submit"
          @click="submit"
        >
          {{ createMutation.isPending.value ? 'Creating…' : 'Create' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
